/*
 * Session storage for the task pane.
 *
 * The only thing kept client-side is the opaque session token. The client ID
 * and API key are handed to the BFF once, exchanged for that token, and never
 * written anywhere in the browser.
 *
 * Storage is localStorage keyed with Office.context.partitionKey, the pattern
 * Microsoft documents for isolating add-in data per context. Office.context
 * .roamingSettings is deliberately NOT used: Microsoft documents it as
 * insecure storage — it is readable through Microsoft Graph. It is fine for
 * non-sensitive preferences such as the last project used, and nothing else.
 */

/* global localStorage, Office */

export interface SessionUser {
  id: string;
  displayName: string;
  email: string;
}

export interface Session {
  token: string;
  /** RFC 3339, as returned by the BFF. */
  expiresAt: string;
  user: SessionUser;
  /** Kept so the pane can identify the account when displayName is empty. */
  clientId: string;
}

const STORAGE_KEY = "valoon.session";

/**
 * Builds the storage key for a partition. Exported for testing, and pure so
 * the partitioning rule can be verified without an Office host.
 *
 * An absent partition key is normal — only some hosts partition storage — and
 * the documented behaviour is to fall back to the bare key.
 */
export function partitionedKey(
  partitionKey: string | undefined,
  key: string = STORAGE_KEY
): string {
  return partitionKey ? partitionKey + key : key;
}

/**
 * Reports whether a session is still usable, with a small margin so a request
 * that is about to be sent does not race the expiry.
 */
export function isLive(session: Session, now: Date = new Date()): boolean {
  const expiresAt = Date.parse(session.expiresAt);
  if (Number.isNaN(expiresAt)) {
    // A session we cannot date is a session we cannot trust.
    return false;
  }
  const marginMs = 30_000;
  return expiresAt - marginMs > now.getTime();
}

/** Parses a stored session, returning null for anything unusable. */
export function parseSession(raw: string | null, now: Date = new Date()): Session | null {
  if (!raw) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  const session = parsed as Partial<Session>;
  if (
    !session ||
    typeof session.token !== "string" ||
    !session.token ||
    typeof session.expiresAt !== "string"
  ) {
    return null;
  }
  const full: Session = {
    token: session.token,
    expiresAt: session.expiresAt,
    clientId: typeof session.clientId === "string" ? session.clientId : "",
    user: {
      id: session.user?.id ?? "",
      displayName: session.user?.displayName ?? "",
      email: session.user?.email ?? "",
    },
  };
  return isLive(full, now) ? full : null;
}

function currentPartitionKey(): string | undefined {
  // Office is loaded from the CDN even outside an Office host, but
  // Office.context only exists once a host has initialised it.
  try {
    return typeof Office !== "undefined" ? Office.context?.partitionKey : undefined;
  } catch {
    return undefined;
  }
}

export function loadSession(): Session | null {
  try {
    return parseSession(localStorage.getItem(partitionedKey(currentPartitionKey())));
  } catch {
    // Storage can be unavailable (disabled cookies, private mode); the pane
    // treats that as "not signed in" rather than crashing.
    return null;
  }
}

export function saveSession(session: Session): void {
  try {
    localStorage.setItem(partitionedKey(currentPartitionKey()), JSON.stringify(session));
  } catch {
    // Nothing useful to do: the sign-in still worked for this pane's lifetime.
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(partitionedKey(currentPartitionKey()));
  } catch {
    // Ignore: see saveSession.
  }
}
